/*
  Entry point and wrapper component for the whole application

  Handles user login + session management, including token expiration
  Handles restarting a user session if still active, including taking them to the last page they visited
  Handles data filters, so that values can be remembered during a session
  Handles toast notifications 
  Handles processing requests and indicating active requests to the user

  At some later point I might try to split this up into smaller chunks
*/

import React, { Suspense } from 'react';
import './Styles/App.css';

import { Switch, Route, Redirect, withRouter } from "react-router-dom";

import MainContext from "./MainContext";

import { login, getUserRole, getAccessDetails, getUserDetails } from "./Requests/UserRequests";
import { getLocations } from "./Requests/UtilityRequests";
import BaseValues, { setSSOToken, keepTestingConnection, stopTestingConnection } from "./Requests/BaseValues";

import { Lookup } from "./Constants";

import LoginPage from "./Containers/LoginPage";

const HasUserContainer = React.lazy(() => import('./Containers/HasUserContainer'));


const sessionTime = (5*60*60*1000);
const sessionWarningTime = (5*60*1000);

const defaultState = {
  user: null,
  currentRoute: null,
  currentSubRoute: null,
  hasFilter: null,
  filterOpen: null,
  message: null,
  processingList: [],
  filterValues: null,
  reportFilterValues: null,
  pathToggle: false,
  applicationMode: "",
  registrationModal: false,
  loggedOut: false,
  normalOperation: true,
  isDisconnected: false,
  isServerError: false,
  query: null,
  clearSwitchCheckInterval: null
};

// store these values outside of state
// they're related to the session/token expiration
let scheduledExpirationReminder = null;
let scheduledAutoLogout = null;


class App extends React.Component {

  static contextType = MainContext;

  state = defaultState;

  // on initial load, check whether there's still an active user token
  // - if so, start a new session for them
  // - if not, clear out any leftover user information
  componentDidMount(){

    // check whether the user exists yet in localStorage
    if(BaseValues.APIKey){
      // check on the location list to see if the saved token is still valid
      getLocations()
      .then(({data}) => {
        if(data && data.length > 0){
          console.log("WE HAVE A USER ALREADY");
          console.log("we have a user already", data);
          // set the user state based on localStorage
          this.constructUser(this.takeRouteFromStorage);
        }
        else{
          console.log("unable to get the user");
          // clear the localStorage entries and logout
          this.attemptLogout();
        }
      });
    }

    this.testConnection();
  }

    //
  // call on the base test connection function
  //
  testConnection = () => {
    // no setup, right?

    keepTestingConnection(
      this.disconnectionFunc,
      this.serverErrorFunc,
      this.returnToSuccessFunc
    );
  };

  //
  // these functions will only be called when the status of the connection changes
  //
  disconnectionFunc = () => {
    // alert("DISCONNECTED!");
    this.setState({
      normalOperation: false,
      isDisconnected: true,
      isServerError: false
    });
  };

  serverErrorFunc = () => {
    // alert("SERVER ERROR");
    this.setState({
      normalOperation: false,
      isDisconnected: false,
      isServerError: true
    });
  };

  returnToSuccessFunc = () => {
    // alert("NORMAL FUNCTIONALITY RETURNED");
    this.setState({
      normalOperation: true,
      isDisconnected: false,
      isServerError: false
    });
  }

///////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////

  //
  // log out the user, removing data from localstorage
  //
  attemptLogout = () => {
    // also need to clear the user entries from localStorage
    this.clearUserFromLocalStorage();
    const logoutState = {...defaultState, loggedOut: true};
    console.log("LOGOUT STATE", logoutState);
    this.setState(logoutState);

    this.removeRouteFromStorage();

    //
    // if we performed a logout due to a token expiration
    // (either timeout during a session or if token already expired when they opened),
    // then inform the user of this
    //

    if(scheduledExpirationReminder){
      console.log("we had a timeout pending", scheduledExpirationReminder);
      clearTimeout(scheduledExpirationReminder);
      scheduledExpirationReminder = null;
    }

    if(scheduledAutoLogout){
      console.log("we had a timeout pending", scheduledAutoLogout);
      clearTimeout(scheduledAutoLogout);
      scheduledAutoLogout = null;
    }

    if(this.state.clearSwitchCheckInterval && typeof this.state.clearSwitchCheckInterval === "function"){
      // this shouldn't really be necessary, as it should be handled on the component unmount already
        this.state.clearSwitchCheckInterval();
    }
  }

  //
  // attempt to log in the users with the credentials from the login page
  // if it works, hand off to processSuccessfulLogin()
  //
  attemptLogin = (userdata, token) => {
    // console.log("attempt login");

    // if we're handed a token here, then it means this is being
    // called from a successful SSO login
    if(token) {
      console.log("SSO: process successful SSO login");
      this.processSuccessfulLogin(userdata.username, token, true);
      return;
    }

    // otherwise this is being called with a username + password
    const request = () => login(userdata);
    const then = ({data}) => {

      console.log(userdata, data);

      if(!data || data.Authenticated === "false" || !data.UserToken || data.UserToken === ""){
        // login failed... have something prepared for this one...
        console.log("LOGIN FAILED, NO TOKEN");
        this.sendNotification(true, "Invalid Username + Password Combination");
      }
      else{
        console.log("process successful login");
        this.processSuccessfulLogin(userdata.username, data.UserToken);
      }
    };
    this.runProcessing(request, then, "login");
  }

  //
  // after login, get the user's role,
  // write their information to localstorage
  // and then pass off to constructUser()
  //
  processSuccessfulLogin = (username, token, isSSO) => {
    const request = () => getUserRole(username);
    const then = ({data}) => {

      console.log("GET ROLE", data);

      if(data && data.length > 0){
        // first write values to localstorage
        this.writeUserToLocalStorage(username, data[0].Role, data[0].Group, token, isSSO);
        // then create the object
        this.constructUser();
      }
      else{
        console.log("FAILING HERE 1", data, data.length);
        this.setState({registrationModal: true});
      }
    };
    this.runProcessing(request, then, "getUserRole");
  }

  //
  // create the user object,
  // fill in user access
  // get user details
  // set state based on all of the data we have so far (as well as the default routes)
  // check whether the current API token is still valid or not
  //
  constructUser = (callback) => {
    // create the user object from the values which are in localstorage
    const user = this.getUserFromLocalStorage();

    console.log("constructed user initial", user);

    // get their routing access based on their role
    user.access = getAccessDetails(user.role);

    console.log("ACCESS", user.access);

    const request = () => getUserDetails(user.username);
    const then = ({data}) => {

      console.log("USER DETAILS RETURN", data, data.length, data && data.length);

      if(data && data.length){

        if(data.length === 1 && !data[0].userexists) {
          console.log("FAILING HERE 2", data, data.length, !data[0].userexists);
          this.displayUserRegistrationMessage();
          return;
        }

        // fill in the user details
        // first + last name
        // current location + lanes

        console.log("WE HAVE DATA FOR THE USER", data);

        // set user location info and user current location/gates
        user.locationData = data;
        user.currentLocation = data[0];
        user.firstName = data[0].userfname;
        user.lastName = data[0].userlname;

        // we get back a list of their recent locations
        // and we have to pick out the most recent one
        for(let i = 0; i < data.length; i++){
          const date1 = new Date(user.currentLocation.requesttime);
          const date2 = new Date(data[i].requesttime);

          this.removeBlankLanes(data[i]);

          if(date2 > date1){
            user.currentLocation = data[i];
          }
        }
      }

      console.log("COMPLETED USER", user);

      //
      // big initial setting of state
      //
      this.setState({
        user,
        applicationMode: user.groupid, // bus, lightrail, etc... only bus for now
        currentRoute: user.access.defaultRoute,
        currentSubRoute: user.access.defaultRoute.defaultSubpage,
        hasFilter: user.access.defaultRoute.defaultSubpage 
                    && user.access.defaultRoute.defaultSubpage.hasFilter,
        filterOpen: user.access.defaultRoute.defaultSubpage 
                    && user.access.defaultRoute.defaultSubpage.hasFilter
                    && user.access.defaultRoute.defaultSubpage.filterOpenByDefault
      }, () => {
        if(callback && typeof callback === "function"){
          console.log("we have a callback here");
          callback();
        }
      });

      // after all that, make sure that the user's session is still valid
      this.checkUserKeyExpiration();
    };
    this.runProcessing(request, then, "getUserDetails");
  }

  //
  // get the user's new information from the back end
  // after the select a new location/lanes for BusOps trips
  //
  updateUserDetails = () => {
    const {user} = this.state;

    const request = () => getUserDetails(user.username);
    const then = ({data}) => {

      console.log("USER DETAILS RETURN", data);

      if(data && data.length){
        // set user location info and user current location/gates
        user.locationData = data;
        user.currentLocation = data[0];
        for(let i = 0; i < data.length; i++){
          const date1 = new Date(user.currentLocation.requesttime);
          const date2 = new Date(data[i].requesttime);

          this.removeBlankLanes(data[i]);

          if(date2 > date1){
            user.currentLocation = data[i];
          }
        }
      }

      console.log("COMPLETED USER", user);

      this.setState({user});
    };
    this.runProcessing(request, then, "getUserDetails");
  }

  //
  // if for some reason the user has blank lane entries associated with themselves,
  // this will clear them out so they don't appear in the display
  //
  removeBlankLanes = (location) => {
    const cleanLanes = [];

    const split = location.assigned_lane_gate.split(",");

    for(let i = 0; i < split.length; i++){
      if(split[i]){
        cleanLanes.push(split[i]);
      }
    }

    location.assigned_lane_gate = cleanLanes;
  }

  //
  // write the user's information to localstorage,
  // so it can be reused if they come back while their current token
  // is still active
  //
  writeUserToLocalStorage = (username, role, groupid, token, isSSO) => {
    localStorage.setItem("__user_name__", username);
    localStorage.setItem("__user_role__", role);
    localStorage.setItem("__user_group__", groupid);
    if(isSSO){
      setSSOToken(token);
    }
    else {
      localStorage.setItem("__user_api_key__", token);
    }

    let expiration = (new Date()).setTime((new Date()).getTime() + sessionTime);

    console.log("expiration time", expiration);

    localStorage.setItem("__user_key_expiration__", expiration);

    this.checkUserKeyExpiration();
  }

  //
  // retrieve the user data from localstorage
  //
  getUserFromLocalStorage = () => {
    return {
      username:  localStorage.getItem("__user_name__"),
      role: localStorage.getItem("__user_role__"), 
      groupid: localStorage.getItem("__user_group__"), 
      access: null,
      Location: null, // these two are placeholders for now
      Gates: []
    };
  }

  //
  // check how much time there is left for the user's current token
  // if we're past that time, log out immediately,
  // if we're within 5 minutes of that time give a warning,
  // otherwise set two timers:
  // - one to go off with 5 minutes left to give the user a warning
  // - the other to log the user out after the token time is up
  //
  checkUserKeyExpiration = () => {
    const exp = new Date(parseInt(localStorage.getItem("__user_key_expiration__")));
    const now = new Date();

    // get the difference between the current time and the time of token expiration
    const diff = (exp - now);
    const diffMinusWarning = diff - sessionWarningTime;

    console.log("session time diff", localStorage.getItem("__user_key_expiration__"), {exp, now, diffMinusWarning});

    const runTimeWarning = () => {
      console.log("THE EXPIRATION CALLBACK", scheduledExpirationReminder);
      if(scheduledExpirationReminder){
        alert("Your session is set to expire soon. To avoid unexpected disconnection, please log out and log back in as soon as possible.");
        clearTimeout(scheduledExpirationReminder);
        scheduledExpirationReminder = null;
      }
    }

    // we're past the limit already
    if(diff <= 0){
      // just logout immediately
      console.log("session has expired");
      this.attemptLogout();
      return;
    }
    else{
      // set the auto logout timer
      scheduledAutoLogout = setTimeout(this.attemptLogout, diff);
    }

    // give the user a warning
    if(diffMinusWarning < 0){
      // only five minutes left
      runTimeWarning();
    }
    else{
      // create the timeout event
      scheduledExpirationReminder = setTimeout(runTimeWarning, diffMinusWarning);
      console.log("expiration reminder", scheduledExpirationReminder);
    }
  }

  //
  // remove the current user data from localstorage
  // - this is used on logout
  //
  clearUserFromLocalStorage = () => {
    localStorage.removeItem("__user_name__");
    localStorage.removeItem("__user_role__");
    localStorage.removeItem("__user_group__");
    localStorage.removeItem("__user_api_key__");
    localStorage.removeItem("__user_key_expiration__");
    setSSOToken(null);
  }

  //
  //
  //
  // For when the SSO succeeds but ther user hasn't registered
  //
  //
  //
  displayUserRegistrationMessage = () => {
    this.setState({registrationModal: true});
  }


///////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////

  // 
  // save the current route to localstorage,
  // so we can navigate the user back here
  //
  saveRouteToStorage = (path, route, subroute) => {
    localStorage.setItem("__user_path__", path);
    localStorage.setItem("__user_route__", route.name);
    localStorage.setItem("__user_subroute__", subroute ? subroute.name : null);

    console.log("RECORDING THE ROUTE TO STORAGE", path, route, subroute);
  }

  //
  // retrieve the last route the user visited from localstorage
  // and then navigate to that route
  //
  takeRouteFromStorage = () => {
    // get the objects related to the string values in storage
    // if they exists, then use them to call the takeRoute method
    
    const path = localStorage.getItem("__user_path__");
    let route = localStorage.getItem("__user_route__");
    let subroute = localStorage.getItem("__user_subroute__");

    console.log("TAKE ROUTE FROM STORAGE");
    console.log("LOADING THE PAGE", {path, route, subroute});
    
    // find the route/subroute objects from lookup
    route = route ? Lookup[route] : null;
    subroute = subroute ? Lookup[subroute] : null;

    // and use them to go there
    if(path && route) {
      console.log("TAKE THE ROUTE FROM STORAGE HERE");
      this.takeRoute(path, route, subroute);
    }

    this.setState({pathToggle: !this.state.pathToggle});
  }

  //
  // remove the current route from storage,
  // - used on logout
  //
  removeRouteFromStorage = () => {
    localStorage.removeItem("__user_path__");
    localStorage.removeItem("__user_route__");
    localStorage.removeItem("__user_subroute__");
  }

  //
  // 
  //
  takeRoute = (path, newRoute, newSubRoute = null, blockRedirect = false, query = null) => {
    // console.log(evt.target.attributes.link.value);

    console.log("TAKE ROUTE", {path, newRoute, newSubRoute, query});

    // method for actually redirecting the app to the new route
    const runRedirect = () => {
      console.log("TEST FOR REDIRECT");
      // we might block if we're just updating the state to match the current route
      if(!blockRedirect){
        console.log("RUN REDIRECT", path + (query ? this.createQueryString(query) : ""), this.state);
        this.props.history.push(path + (query ? this.createQueryString(query) : ""));
      }
    }

    // the general pattern for updating the state information:
    // - check whether the new route has a filter and whether the filter is open by default
    // - set the state based on the route, subroute, and the above filter info
    // - run the redirect itself
    // - save the new route to state

    if(newRoute && newSubRoute){
      //
      // this is a call from takeRouteFromStorage
      // run the logic in sequence
      //

      console.log("ROUTE A");

      let hasFilter = false;
      let filterOpen = false;

      if(newRoute.defaultSubpage){
        hasFilter = newRoute.defaultSubpage.hasFilter;
        filterOpen = newRoute.defaultSubpage.filterOpenByDefault;
      }
      else{
        console.log("THE NEW ROUTE", newRoute);
        hasFilter = newRoute.hasFilter;
        filterOpen = newRoute.filterOpenByDefault;
      }

      this.setState(
        {
          currentRoute: newRoute, 
          currentSubRoute: newRoute.defaultSubpage, 
          hasFilter,
          filterOpen,
          query
        }, () => {
          // after the main route is set, do the same for the subroute
          this.setState(
            {
              currentSubRoute: newSubRoute, 
              hasFilter: newSubRoute.hasFilter,
              filterOpen: newSubRoute.filterOpenByDefault
            }, () => {
            runRedirect();
          });
      });

      // no need to save the route to state here, since that's where we got it from in the first place
    }
    else if(newRoute){
      //
      // new main route
      //

      console.log("ROUTE B");

      let hasFilter = false;
      let filterOpen = false;

      if(newRoute.defaultSubpage){
        hasFilter = newRoute.defaultSubpage.hasFilter;
        filterOpen = newRoute.defaultSubpage.filterOpenByDefault;
      }
      else{
        console.log("NEW ROUTE 2", newRoute);
        hasFilter = newRoute.hasFilter;
        filterOpen = newRoute.filterOpenByDefault;
      }

      this.setState(
        {
          currentRoute: newRoute, 
          currentSubRoute: newRoute.defaultSubpage, 
          hasFilter,
          filterOpen,
          query
        }, () => {
        // this.props.history.push(path);
        runRedirect();
        this.saveRouteToStorage(path, this.state.currentRoute, null);
      });
    }
    else if(newSubRoute){
      //
      // new subroute
      //

      console.log("ROUTE C");

      this.setState(
        {
          currentSubRoute: newSubRoute, 
          hasFilter: newSubRoute.hasFilter,
          filterOpen: newSubRoute.filterOpenByDefault,
          query
        }, () => {
        // this.props.history.push(path);
        runRedirect();
        this.saveRouteToStorage(path, this.state.currentRoute, this.state.currentSubRoute);
      });
    }
  }

  //
  // expects an array of values {key: "", value: ""}
  //
  createQueryString = (query) => {
    if(query && query.length){
      let ret = "?";

      query.forEach(q => {
        ret += `${q.key}=${q.value}`;
      });

      return ret;
    }
    return "";
  }


///////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////

  //
  // switch the current page's filter between being displayed and being hidden
  //
  toggleFilter = (evt, val, callback) => {
    console.log("TOGGLE THE FILTER");
    const filterOpen = val !== undefined ? val : !this.state.filterOpen;
    this.setState({filterOpen}, () => callback && callback());
  }

  //
  // update the values in the current page's filter
  //
  updateFilter = (values) => {
    this.setState({filterValues: values});
  }

  //
  // make a special case for remembering the filter values
  // for the individual report pages, so they can each be
  // specifically reconstituted during the session
  //
  updateReportFilter = (values) => {
    if(this.state.reportFilterValues){
      console.log("UPDATE REPORT FILTER", values);
      const copy = {...this.state.reportFilterValues};

      // only change the keys that are available
      for(let key in values){
        copy[key] = values[key];
      }
      this.setState({reportFilterValues: copy});
      return;
    }

    console.log("SET REPORT FILTER", values);

    this.setState({reportFilterValues: values});
  }

///////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////

  //
  // display a toast notification, then hide after 2.5 seconds
  //
  sendNotification = (isError, text) => {
    this.setState({message: {isError, text}}, () => {
      setTimeout(() => {
        this.setState({message:null})
      }, 2500);
    })
  }

///////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////

  //
  // shell for handling all API requests in a consistent way
  // while allowing the user to be informed of the processing status
  //
  // keep track of a list of requests that are active at any point in time,
  // so that we can accurately inform the user when they've all completed
  //
  runProcessing = (request, callback, name) => {

    // only display the processing banner if the request
    // takes longer than 100ms to complete
    // I don't want to flash it at the user all the time
    let processingTimeout = setTimeout(() => {
      const processingCopy = [...this.state.processingList, name];
      console.log("ADDING REQUEST", name, processingCopy);
      this.setState({processingList: processingCopy});
    }, 100);

    // run the request
    request().then((...res) => {
      console.log("DONE WITH REQUEST", name, res, callback);

      if(processingTimeout){
        clearTimeout(processingTimeout);
      }

      callback(...res);
      // it would honestly be better if this last part were also run as a callback
      // but that would be harder, and demand more consistency from the methods
      // that are being used here 

      // remove this name from the processing list
      const copy = this.state.processingList.filter(v => v !== name);
      this.setState({processingList: copy});
      console.log("REMOVING REQUEST", name, copy);

    }).catch((error) => {
      console.log("There's been an error", error);
      if(error.errorMessage === "Invalid Token.  Please authenticate user."){
        // the user's session might have expired just before the request was completed
        this.attemptLogout();
      }
      else{
        this.sendNotification(true, "There's been an error, please try again");
      }

      if(processingTimeout){
        clearTimeout(processingTimeout);
      }

      // remove this name from the processing list
      const copy = this.state.processingList.filter(v => v !== name);
      this.setState({processingList: copy});
    });
  }

///////////////////////////////////////////////////
///////////////////////////////////////////////////
///////////////////////////////////////////////////
///////////////////////////////////////////////////
///////////////////////////////////////////////////

  setClearSwitchCheckInterval = (f) => {
    if(f && typeof f === "function"){
      this.setState({clearSwitchCheckInterval: f});
    }
  }


  /////////////////////////////////////////////////////////////////////////
  /////////////////////////////////////////////////////////////////////////
  /////////////////////////////////////////////////////////////////////////
  /////////////////////////////////////////////////////////////////////////
  /////////////////////////////////////////////////////////////////////////
  /////////////////////////////////////////////////////////////////////////

  //
  // OK, lets' actually render this thing now
  //
  // we handle the root route here, and then pass the rest off to the HasUserContainer
  //
  render() {
    return (
      <div className="App" style={{position: "fixed", top: "0px", width: "100vw"}}>
        <MainContext.Provider value={
          {
            // all of the value and methods which will be available throughout the application
            ...this.state, 
            logout: this.attemptLogout, 
            takeRoute: this.takeRoute, 
            toggleFilter: this.toggleFilter,
            sendNotification: this.sendNotification,
            runProcessing: this.runProcessing,
            updateFilter: this.updateFilter,
            updateReportFilter: this.updateReportFilter,
            updateUserDetails: this.updateUserDetails,
            setClearSwitchCheckInterval: this.setClearSwitchCheckInterval
          }}>
          <Switch>
            <Route exact path="/">
              {
                !this.state.user
                ? <>
                  <div>                    
                    <LoginPage attemptLogin={this.attemptLogin} loggedOut={this.state.loggedOut} />
                  </div>
                  </>
                : <Redirect to={this.state.user.access.defaultRoute.path} />
              }
            </Route>
            <Route>
              <Suspense fallback={<div>Loading...</div>}>
                <HasUserContainer 
                  location={this.props.location}
                  path={this.props.location.pathname}
                  pathToggle={this.state.pathToggle}
                  isDisconnected = {this.state.isDisconnected}
                  isServerError = {this.state.isServerError}
                />
              </Suspense>
            </Route>
          </Switch>
        </MainContext.Provider>
        {
          // toast notifications
          this.state.message
          ? <div className={this.state.message.isError ? "notification notification-error": "notification"}>
              {this.state.message.text}
            </div>
          : null
        }
        {
          // processing overlay
          this.state.processingList.length > 0
          ? <div className="processing">
            <span className="processing-text">Processing</span><span className="processing1">.</span><span className="processing2">.</span><span className="processing3">.</span>
          </div>
          : null
        }
        {
          this.state.registrationModal
          ? <div className="registration-modal">
            Please contact the Helpdesk to ask for access
          </div>
          : null
        }
      </div>
    );
  }
}

//
// export as a higher order component with routing
//
export default withRouter(App);
